const buildSuffix = (url?: {
  query?: Record<string, string | number | boolean | Array<string | number | boolean>>,
  hash?: string
}) => {
  const query = url?.query;
  const hash = url?.hash;
  if (!query && !hash) return '';
  const search = (() => {
    if (!query) return '';

    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) =>
          params.append(key, String(item))
        );
      } else {
        params.set(key, String(value));
      }
    });

    return `?${params.toString()}`;
  })();
  return `${search}${hash ? `#${hash}` : ''}`;
};

export const pagesPath = {
  'aces_only': {
    'external_sharing': {
      'expiry_date_extend': {
        $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/external-sharing/expiry-date-extend' as const, hash: url?.hash, path: `/aces-only/external-sharing/expiry-date-extend${buildSuffix(url)}` })
      }
    },
    'sample_highlight_groups': {
      _sampleHighlightGroupUuid: (sampleHighlightGroupUuid: string | number) => ({
        'edit': {
          $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/sample-highlight-groups/[sampleHighlightGroupUuid]/edit' as const, query: { sampleHighlightGroupUuid }, hash: url?.hash, path: `/aces-only/sample-highlight-groups/${sampleHighlightGroupUuid}/edit${buildSuffix(url)}` })
        }
      }),
      'create': {
        $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/sample-highlight-groups/create' as const, hash: url?.hash, path: `/aces-only/sample-highlight-groups/create${buildSuffix(url)}` })
      },
      $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/sample-highlight-groups' as const, hash: url?.hash, path: `/aces-only/sample-highlight-groups${buildSuffix(url)}` })
    },
    'tenant_deactivate': {
      $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/tenant-deactivate' as const, hash: url?.hash, path: `/aces-only/tenant-deactivate${buildSuffix(url)}` })
    },
    'tenant_licences_change': {
      $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/tenant-licences-change' as const, hash: url?.hash, path: `/aces-only/tenant-licences-change${buildSuffix(url)}` })
    },
    'tenant_members_invitation': {
      $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/tenant-members-invitation' as const, hash: url?.hash, path: `/aces-only/tenant-members-invitation${buildSuffix(url)}` })
    },
    'tenant_registration': {
      $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/tenant-registration' as const, hash: url?.hash, path: `/aces-only/tenant-registration${buildSuffix(url)}` })
    },
    'tenant_restrict_all': {
      $url: (url?: { hash?: string }) => ({ pathname: '/aces-only/tenant-restrict-all' as const, hash: url?.hash, path: `/aces-only/tenant-restrict-all${buildSuffix(url)}` })
    }
  },
  'career_plus': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/career-plus/callback' as const, hash: url?.hash, path: `/career-plus/callback${buildSuffix(url)}` })
    }
  },
  'favorites': {
    'meetings': {
      $url: (url?: { hash?: string }) => ({ pathname: '/favorites/meetings' as const, hash: url?.hash, path: `/favorites/meetings${buildSuffix(url)}` })
    },
    'talks': {
      _favoriteTalkUuid: (favoriteTalkUuid: string | number) => ({
        $url: (url?: { hash?: string }) => ({ pathname: '/favorites/talks/[favoriteTalkUuid]' as const, query: { favoriteTalkUuid }, hash: url?.hash, path: `/favorites/talks/${favoriteTalkUuid}${buildSuffix(url)}` })
      }),
      $url: (url?: { hash?: string }) => ({ pathname: '/favorites/talks' as const, hash: url?.hash, path: `/favorites/talks${buildSuffix(url)}` })
    },
    $url: (url?: { hash?: string }) => ({ pathname: '/favorites' as const, hash: url?.hash, path: `/favorites${buildSuffix(url)}` })
  },
  'google_calendar': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/google-calendar/callback' as const, hash: url?.hash, path: `/google-calendar/callback${buildSuffix(url)}` })
    }
  },
  'highlight_groups': {
    _highlightGroupUuid: (highlightGroupUuid: string | number) => ({
      'edit': {
        $url: (url?: { hash?: string }) => ({ pathname: '/highlight-groups/[highlightGroupUuid]/edit' as const, query: { highlightGroupUuid }, hash: url?.hash, path: `/highlight-groups/${highlightGroupUuid}/edit${buildSuffix(url)}` })
      }
    }),
    'create': {
      $url: (url?: { hash?: string }) => ({ pathname: '/highlight-groups/create' as const, hash: url?.hash, path: `/highlight-groups/create${buildSuffix(url)}` })
    },
    $url: (url?: { hash?: string }) => ({ pathname: '/highlight-groups' as const, hash: url?.hash, path: `/highlight-groups${buildSuffix(url)}` })
  },
  'initial_settings': {
    $url: (url?: { hash?: string }) => ({ pathname: '/initial-settings' as const, hash: url?.hash, path: `/initial-settings${buildSuffix(url)}` })
  },
  'meeting': {
    _meetingId: (meetingId: string | number) => ({
      'edit': {
        $url: (url?: { hash?: string }) => ({ pathname: '/meeting/[meetingId]/edit' as const, query: { meetingId }, hash: url?.hash, path: `/meeting/${meetingId}/edit${buildSuffix(url)}` })
      },
      'external_sharing': {
        'preview': {
          $url: (url?: { hash?: string }) => ({ pathname: '/meeting/[meetingId]/external-sharing/preview' as const, query: { meetingId }, hash: url?.hash, path: `/meeting/${meetingId}/external-sharing/preview${buildSuffix(url)}` })
        }
      },
      $url: (url?: { hash?: string }) => ({ pathname: '/meeting/[meetingId]' as const, query: { meetingId }, hash: url?.hash, path: `/meeting/${meetingId}${buildSuffix(url)}` })
    }),
    'create': {
      $url: (url?: { hash?: string }) => ({ pathname: '/meeting/create' as const, hash: url?.hash, path: `/meeting/create${buildSuffix(url)}` })
    },
    $url: (url?: { hash?: string }) => ({ pathname: '/meeting' as const, hash: url?.hash, path: `/meeting${buildSuffix(url)}` })
  },
  'microsoft': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/microsoft/callback' as const, hash: url?.hash, path: `/microsoft/callback${buildSuffix(url)}` })
    },
    'connection': {
      $url: (url?: { hash?: string }) => ({ pathname: '/microsoft/connection' as const, hash: url?.hash, path: `/microsoft/connection${buildSuffix(url)}` })
    }
  },
  'note_templates': {
    _noteTemplateId: (noteTemplateId: string | number) => ({
      'edit': {
        $url: (url?: { hash?: string }) => ({ pathname: '/note-templates/[noteTemplateId]/edit' as const, query: { noteTemplateId }, hash: url?.hash, path: `/note-templates/${noteTemplateId}/edit${buildSuffix(url)}` })
      }
    }),
    'create': {
      $url: (url?: { hash?: string }) => ({ pathname: '/note-templates/create' as const, hash: url?.hash, path: `/note-templates/create${buildSuffix(url)}` })
    },
    $url: (url?: { hash?: string }) => ({ pathname: '/note-templates' as const, hash: url?.hash, path: `/note-templates${buildSuffix(url)}` })
  },
  'opening': {
    'step_1': {
      $url: (url?: { hash?: string }) => ({ pathname: '/opening/step-1' as const, hash: url?.hash, path: `/opening/step-1${buildSuffix(url)}` })
    },
    'step_2': {
      $url: (url?: { hash?: string }) => ({ pathname: '/opening/step-2' as const, hash: url?.hash, path: `/opening/step-2${buildSuffix(url)}` })
    },
    'step_3': {
      $url: (url?: { hash?: string }) => ({ pathname: '/opening/step-3' as const, hash: url?.hash, path: `/opening/step-3${buildSuffix(url)}` })
    }
  },
  'outlook_calendar': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/outlook-calendar/callback' as const, hash: url?.hash, path: `/outlook-calendar/callback${buildSuffix(url)}` })
    }
  },
  'password_change': {
    $url: (url?: { hash?: string }) => ({ pathname: '/password-change' as const, hash: url?.hash, path: `/password-change${buildSuffix(url)}` })
  },
  'profile_change': {
    $url: (url?: { hash?: string }) => ({ pathname: '/profile-change' as const, hash: url?.hash, path: `/profile-change${buildSuffix(url)}` })
  },
  'salesforce': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/salesforce/callback' as const, hash: url?.hash, path: `/salesforce/callback${buildSuffix(url)}` })
    }
  },
  'settings': {
    'account': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/account' as const, hash: url?.hash, path: `/settings/account${buildSuffix(url)}` })
    },
    'account_security': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/account-security' as const, hash: url?.hash, path: `/settings/account-security${buildSuffix(url)}` })
    },
    'display_setting': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/display-setting' as const, hash: url?.hash, path: `/settings/display-setting${buildSuffix(url)}` })
    },
    'download_restrictions': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/download-restrictions' as const, hash: url?.hash, path: `/settings/download-restrictions${buildSuffix(url)}` })
    },
    'groups': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/groups' as const, hash: url?.hash, path: `/settings/groups${buildSuffix(url)}` })
    },
    'integration_calendar': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/integration-calendar' as const, hash: url?.hash, path: `/settings/integration-calendar${buildSuffix(url)}` })
    },
    'integration_crm': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/integration-crm' as const, hash: url?.hash, path: `/settings/integration-crm${buildSuffix(url)}` })
    },
    'integration_video': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/integration-video' as const, hash: url?.hash, path: `/settings/integration-video${buildSuffix(url)}` })
    },
    'integration_zoom_admin': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/integration-zoom-admin' as const, hash: url?.hash, path: `/settings/integration-zoom-admin${buildSuffix(url)}` })
    },
    'members': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/members' as const, hash: url?.hash, path: `/settings/members${buildSuffix(url)}` })
    },
    'notification_settings': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/notification-settings' as const, hash: url?.hash, path: `/settings/notification-settings${buildSuffix(url)}` })
    },
    'onsite_speaker_diarization': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/onsite-speaker-diarization' as const, hash: url?.hash, path: `/settings/onsite-speaker-diarization${buildSuffix(url)}` })
    },
    'tenant_change': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/tenant-change' as const, hash: url?.hash, path: `/settings/tenant-change${buildSuffix(url)}` })
    },
    'tenant_dictionary_entries': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/tenant-dictionary-entries' as const, hash: url?.hash, path: `/settings/tenant-dictionary-entries${buildSuffix(url)}` })
    },
    'tenant_integration_phone': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/tenant-integration-phone' as const, hash: url?.hash, path: `/settings/tenant-integration-phone${buildSuffix(url)}` })
    },
    'tenant_security': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/tenant-security' as const, hash: url?.hash, path: `/settings/tenant-security${buildSuffix(url)}` })
    },
    'user_integration_phone': {
      $url: (url?: { hash?: string }) => ({ pathname: '/settings/user-integration-phone' as const, hash: url?.hash, path: `/settings/user-integration-phone${buildSuffix(url)}` })
    }
  },
  'watchlists': {
    _watchlistUuid: (watchlistUuid: string | number) => ({
      $url: (url?: { hash?: string }) => ({ pathname: '/watchlists/[watchlistUuid]' as const, query: { watchlistUuid }, hash: url?.hash, path: `/watchlists/${watchlistUuid}${buildSuffix(url)}` })
    }),
    $url: (url?: { hash?: string }) => ({ pathname: '/watchlists' as const, hash: url?.hash, path: `/watchlists${buildSuffix(url)}` })
  },
  'zoom': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/zoom/callback' as const, hash: url?.hash, path: `/zoom/callback${buildSuffix(url)}` })
    }
  },
  'zoom_admin': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/zoom-admin/callback' as const, hash: url?.hash, path: `/zoom-admin/callback${buildSuffix(url)}` })
    }
  },
  'zoom_phone': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/zoom_phone/callback' as const, hash: url?.hash, path: `/zoom_phone/callback${buildSuffix(url)}` })
    }
  },
  'auth': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/auth/callback' as const, hash: url?.hash, path: `/auth/callback${buildSuffix(url)}` })
    }
  },
  'invitation': {
    'callback': {
      $url: (url?: { hash?: string }) => ({ pathname: '/invitation/callback' as const, hash: url?.hash, path: `/invitation/callback${buildSuffix(url)}` })
    },
    $url: (url?: { hash?: string }) => ({ pathname: '/invitation' as const, hash: url?.hash, path: `/invitation${buildSuffix(url)}` })
  },
  'login': {
    $url: (url?: { hash?: string }) => ({ pathname: '/login' as const, hash: url?.hash, path: `/login${buildSuffix(url)}` })
  },
  'maintenance': {
    $url: (url?: { hash?: string }) => ({ pathname: '/maintenance' as const, hash: url?.hash, path: `/maintenance${buildSuffix(url)}` })
  },
  'shared_meetings': {
    _uuid: (uuid: string | number) => ({
      $url: (url?: { hash?: string }) => ({ pathname: '/shared-meetings/[uuid]' as const, query: { uuid }, hash: url?.hash, path: `/shared-meetings/${uuid}${buildSuffix(url)}` })
    })
  },
  $url: (url?: { hash?: string }) => ({ pathname: '/' as const, hash: url?.hash, path: `/${buildSuffix(url)}` })
};

export type PagesPath = typeof pagesPath;
